import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type GmailConnectionStatus = "disconnected" | "connected" | "expired" | "loading";
type SyncStatus = "idle" | "syncing" | "paused" | "completed" | "error";

interface GmailConnection {
  id: string;
  status: string;
  last_sync_at: string | null;
}

interface SyncState {
  status: SyncStatus;
  total_emails: number;
  processed_emails: number;
  imported_documents: number;
  skipped_documents: number;
  error_message: string | null;
}

export function useGmailConnection() {
  const [status, setStatus] = useState<GmailConnectionStatus>("loading");
  const [connection, setConnection] = useState<GmailConnection | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncState, setSyncState] = useState<SyncState | null>(null);
  const syncFnRef = useRef<((pageToken?: string) => Promise<void>) | null>(null);
  const hasAttemptedResume = useRef(false);

  const checkConnection = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setStatus("disconnected");
        return;
      }

      // Use safe view without OAuth tokens
      const { data, error } = await supabase
        .from("connected_accounts_safe")
        .select("id, status, last_sync_at")
        .eq("user_id", session.user.id)
        .eq("provider", "gmail")
        .maybeSingle();

      if (error) {
        console.error("Error checking Gmail connection:", error);
        setStatus("disconnected");
        return;
      }

      if (!data) {
        setStatus("disconnected");
        setConnection(null);
        return;
      }

      setConnection(data);
      setStatus(data.status === "active" ? "connected" : "expired");

      // Also fetch sync state
      const { data: stateData } = await supabase
        .from("gmail_sync_state")
        .select("*")
        .eq("user_id", session.user.id)
        .maybeSingle();

      if (stateData) {
        setSyncState({
          status: stateData.status as SyncStatus,
          total_emails: stateData.total_emails || 0,
          processed_emails: stateData.processed_emails || 0,
          imported_documents: stateData.imported_documents || 0,
          skipped_documents: stateData.skipped_documents || 0,
          error_message: stateData.error_message,
        });
        
        // Auto-resume sync if it was interrupted (status is syncing with a next_page_token)
        if (stateData.status === "syncing" && stateData.next_page_token) {
          return { shouldResume: true, pageToken: stateData.next_page_token };
        }
      }
      
      return { shouldResume: false };
    } catch (err) {
      console.error("Error checking Gmail connection:", err);
      setStatus("disconnected");
    }
  };

  // Subscribe to real-time sync state updates
  useEffect(() => {
    const setupRealtimeSubscription = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const channel = supabase
        .channel('gmail_sync_state_changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'gmail_sync_state',
            filter: `user_id=eq.${session.user.id}`,
          },
          (payload) => {
            const newState = payload.new as any;
            if (newState) {
              setSyncState({
                status: newState.status as SyncStatus,
                total_emails: newState.total_emails || 0,
                processed_emails: newState.processed_emails || 0,
                imported_documents: newState.imported_documents || 0,
                skipped_documents: newState.skipped_documents || 0,
                error_message: newState.error_message,
              });

              // Auto-stop syncing flag when not actively syncing
              if (
                newState.status === "completed" ||
                newState.status === "error" ||
                newState.status === "idle" ||
                newState.status === "paused"
              ) {
                setSyncing(false);
              }
            }
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    };

    setupRealtimeSubscription();
  }, []);

  useEffect(() => {
    checkConnection();

    const params = new URLSearchParams(window.location.search);
    const gmailConnected = params.get("gmail_connected");
    const gmailError = params.get("gmail_error");

    if (gmailConnected === "true") {
      toast.success("Gmail connecté avec succès !");
      window.history.replaceState({}, "", window.location.pathname);
      checkConnection();
    } else if (gmailError) {
      toast.error(`Erreur Gmail: ${gmailError}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);


  const connect = async () => {
    setConnecting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Vous devez être connecté");
        return;
      }

      const { data, error } = await supabase.functions.invoke("gmail-auth", {
        body: {
          user_id: session.user.id,
          return_url: window.location.href,
        },
      });

      if (error) throw error;

      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("URL d'authentification non reçue");
      }
    } catch (err: unknown) {
      console.error("Error connecting Gmail:", err);
      const message = err instanceof Error ? err.message : "Erreur de connexion Gmail";
      toast.error(message);
    } finally {
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { error } = await supabase
        .from("connected_accounts")
        .delete()
        .eq("user_id", session.user.id)
        .eq("provider", "gmail");

      if (error) throw error;

      // Also delete sync state
      await supabase
        .from("gmail_sync_state")
        .delete()
        .eq("user_id", session.user.id);

      setStatus("disconnected");
      setConnection(null);
      setSyncState(null);
      toast.success("Gmail déconnecté");
    } catch (err: unknown) {
      console.error("Error disconnecting Gmail:", err);
      const message = err instanceof Error ? err.message : "Erreur lors de la déconnexion";
      toast.error(message);
    }
  };

  const sync = useCallback(async (pageToken?: string, retryCount = 0) => {
    const MAX_RETRIES = 1;
    setSyncing(true);
    
    try {
      const { data, error } = await supabase.functions.invoke("gmail-sync", {
        body: pageToken ? { pageToken } : {},
      });

      if (error) throw error;

      if (data?.error) {
        throw new Error(data.error);
      }

      // Show progress feedback
      const batchProcessed = data?.batch?.processed || 0;
      const totalProcessed = data?.total?.processed || 0;
      const totalEstimate = data?.total?.estimated || 0;
      
      if (data?.hasMore && totalEstimate > 0) {
        console.log(`Gmail sync progress: ${totalProcessed}/${totalEstimate} emails traités`);
      }

      // If there are more emails to process, auto-continue with robust loop
      if (data?.hasMore && data?.nextPageToken) {
        // Delay between batches to avoid overwhelming the API
        await new Promise(resolve => setTimeout(resolve, 800));
        
        // Recursive call - the loop continues until hasMore is false
        await sync(data.nextPageToken, 0); // Reset retry count on success
      } else {
        // Sync complete
        setSyncing(false);
        
        const imported = data?.total?.imported || 0;
        const skipped = data?.total?.skipped || 0;
        
        if (imported > 0) {
          toast.success(
            `Synchronisation terminée: ${imported} document(s) importé(s)${skipped > 0 ? `, ${skipped} ignoré(s)` : ''}`,
            { duration: 5000 }
          );
        } else if (skipped > 0) {
          toast.info(
            `Synchronisation terminée: ${skipped} document(s) ignoré(s)`,
            { description: "Aucun nouveau document à importer" }
          );
        } else {
          toast.info("Aucun document trouvé dans vos emails");
        }
        
        checkConnection();
      }
    } catch (err: unknown) {
      console.error("Error syncing Gmail:", err);
      
      // Retry logic: if we have retries left, wait and try again
      if (retryCount < MAX_RETRIES && pageToken) {
        console.log(`Gmail sync error, retrying (${retryCount + 1}/${MAX_RETRIES})...`);
        toast.warning("Erreur temporaire, nouvelle tentative...");
        await new Promise(resolve => setTimeout(resolve, 2000));
        await sync(pageToken, retryCount + 1);
        return;
      }
      
      const message = err instanceof Error ? err.message : "Erreur de synchronisation";
      toast.error(message);
      setSyncing(false);
    }
  }, []);

  // Keep ref updated with latest sync function
  useEffect(() => {
    syncFnRef.current = sync;
  }, [sync]);

  // Resume after ref is ready (important: effects run in declaration order)
  useEffect(() => {
    if (hasAttemptedResume.current) return;
    if (syncState?.status !== "syncing") return;

    const resumeSync = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data: stateData } = await supabase
        .from("gmail_sync_state")
        .select("next_page_token")
        .eq("user_id", session.user.id)
        .maybeSingle();

      if (stateData?.next_page_token && syncFnRef.current) {
        hasAttemptedResume.current = true;
        console.log("Resuming interrupted Gmail sync...");
        setSyncing(true);
        syncFnRef.current(stateData.next_page_token);
      }
    };

    resumeSync();
  }, [syncState, sync]);

  const cancelSync = async () => {
    try {
      await supabase.functions.invoke("gmail-sync", {
        body: { action: "cancel" },
      });
      setSyncing(false);
      toast.info("Synchronisation annulée");
    } catch (err) {
      console.error("Error cancelling sync:", err);
    }
  };

  const resetSyncState = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    await supabase
      .from("gmail_sync_state")
      .update({
        status: "idle",
        processed_emails: 0,
        imported_documents: 0,
        skipped_documents: 0,
        next_page_token: null,
        error_message: null,
      })
      .eq("user_id", session.user.id);

    setSyncState(null);
  };

  // Delete all Gmail data (documents, connection, sync state, import settings)
  // Required for GDPR compliance and Google OAuth validation
  const deleteGmailData = async (): Promise<{ deletedCount: number }> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error("Non authentifié");

    // 1. Count and delete all documents imported from Gmail
    const { data: gmailDocs, error: countError } = await supabase
      .from("documents")
      .select("id, file_path")
      .eq("user_id", session.user.id)
      .eq("source", "gmail");

    if (countError) throw countError;

    const deletedCount = gmailDocs?.length || 0;

    // 2. Delete files from storage
    if (gmailDocs && gmailDocs.length > 0) {
      const filePaths = gmailDocs.map(doc => doc.file_path).filter(Boolean);
      if (filePaths.length > 0) {
        await supabase.storage.from("documents").remove(filePaths);
      }

      // 3. Delete document records
      const { error: deleteDocsError } = await supabase
        .from("documents")
        .delete()
        .eq("user_id", session.user.id)
        .eq("source", "gmail");

      if (deleteDocsError) throw deleteDocsError;
    }

    // 4. Delete Gmail connection
    await supabase
      .from("connected_accounts")
      .delete()
      .eq("user_id", session.user.id)
      .eq("provider", "gmail");

    // 5. Delete sync state
    await supabase
      .from("gmail_sync_state")
      .delete()
      .eq("user_id", session.user.id);

    // 6. Delete import settings for Gmail
    await supabase
      .from("import_settings")
      .delete()
      .eq("user_id", session.user.id)
      .eq("provider", "gmail");

    // Update local state
    setStatus("disconnected");
    setConnection(null);
    setSyncState(null);

    return { deletedCount };
  };

  return {
    status,
    connection,
    connecting,
    syncing,
    syncState,
    connect,
    disconnect,
    sync: () => sync(),
    cancelSync,
    resetSyncState,
    deleteGmailData,
    refresh: checkConnection,
  };
}
